import React, { useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import config from "./global/config";

const CashPaymentModal = ({
  open,
  handleClose,
  saleData,
  onPaymentComplete,
}) => {
  const [formData, setFormData] = useState({
    montant_paye: "",
    mode_paiement: "",
    reference_paiement: "",
    commentaire: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const validateForm = useCallback(() => {
    if (!formData.montant_paye || formData.montant_paye <= 0) {
      setError("Please enter a valid payment amount");
      return false;
    }

    if (!formData.mode_paiement) {
      setError("Please select a payment method");
      return false;
    }

    const montantPaye = parseFloat(formData.montant_paye);
    const montantTotal = parseFloat(saleData?.montant_total || 0);

    if (montantPaye > montantTotal) {
      setError("Payment amount cannot exceed the total amount due");
      return false;
    }

    return true;
  }, [formData, saleData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError("");
    setSuccessMessage("");
  };

  const handleSubmit = async () => {
    try {
      if (!validateForm()) {
        return;
      }

      setLoading(true);
      setError("");
      setSuccessMessage("");

      const api = axios.create({
        baseURL: config.apiUrl,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "multipart/form-data",
        },
      });

      // Format date to match MySQL datetime format
      const currentDate = new Date()
        .toISOString()
        .slice(0, 19)
        .replace("T", " ");

      // Prepare payment data
      const paymentData = new FormData();
      paymentData.append("METHOD", "POST");
      paymentData.append("id_vente", saleData.id_vente);
      paymentData.append("categorie_code", "1");
      paymentData.append("date_paiement", currentDate);
      paymentData.append("montant_a_payer", saleData.montant_total.toString());
      paymentData.append("montant_paye", formData.montant_paye.toString());
      paymentData.append("mode_paiement", formData.mode_paiement);
      paymentData.append(
        "reference_paiement",
        formData.reference_paiement || ""
      );
      paymentData.append("commentaire", formData.commentaire || "");
      paymentData.append("user_cre", "SYSTEM");

      // Process payment
      const response = await api.post(
        "paiements_clients_cash.php",
        paymentData
      );

      if (response.data && !response.data.error) {
        // Update sale status and maintain the
        const montantPaye = parseFloat(formData.montant_paye);
        const montantTotal = parseFloat(saleData.montant_total);
        const newStatus = montantPaye >= montantTotal ? "Payé" : "Partiel";

        const saleUpdateData = new FormData();
        saleUpdateData.append("METHOD", "PUT");
        saleUpdateData.append("id_client", saleData.id_client);
        saleUpdateData.append("categorie_code", saleData.categorie_code);
        saleUpdateData.append("numero_vente", saleData.numero_vente);
        saleUpdateData.append("date_vente", saleData.date_vente);
        saleUpdateData.append(
          "montant_total",
          saleData.montant_total.toString()
        );
        saleUpdateData.append("type_paiement", saleData.type_paiement);
        saleUpdateData.append("statut_paiement", newStatus);
        saleUpdateData.append("commentaire", saleData.commentaire || "");
        saleUpdateData.append("user_upd", "SYSTEM");

        await api.post(
          `ventes.php?id_vente=${saleData.id_vente}`,
          saleUpdateData
        );

        setSuccessMessage("Payment processed successfully");
        setTimeout(() => {
          onPaymentComplete();
          handleClose();
          // Reset form
          setFormData({
            montant_paye: "",
            mode_paiement: "",
            reference_paiement: "",
            commentaire: "",
          });
        }, 1500);
      } else {
        throw new Error(response.data?.message || "Payment processing failed");
      }
    } catch (err) {
      console.error("Payment error:", err);
      setError(
        err.response?.data?.message ||
          err.message ||
          "An error occurred while processing payment"
      );
    } finally {
      setLoading(false);
    }
  };

  const remainingAmount = saleData
    ? parseFloat(saleData.montant_total) -
      parseFloat(formData.montant_paye || 0)
    : 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Process Payment</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body1" gutterBottom>
            <strong>Sale Reference:</strong> {saleData?.numero_vente}
          </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>Total Amount:</strong> €{saleData?.montant_total || 0}
          </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>Remaining Amount:</strong> €{remainingAmount.toFixed(2)}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ my: 2 }}>
              {error}
            </Alert>
          )}

          {successMessage && (
            <Alert severity="success" sx={{ my: 2 }}>
              {successMessage}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Amount Paid"
            name="montant_paye"
            type="number"
            value={formData.montant_paye}
            onChange={handleChange}
            margin="normal"
            required
            inputProps={{
              min: 0,
              max: saleData?.montant_total,
              step: "0.01",
            }}
          />

          <FormControl fullWidth margin="normal" required>
            <InputLabel>Payment Method</InputLabel>
            <Select
              name="mode_paiement"
              value={formData.mode_paiement}
              onChange={handleChange}
              label="Payment Method"
            >
              <MenuItem value="Espèces">Cash</MenuItem>
              <MenuItem value="Chèque">Check</MenuItem>
              <MenuItem value="Virement">Bank Transfer</MenuItem>
              <MenuItem value="Carte">Card</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Payment Reference"
            name="reference_paiement"
            value={formData.reference_paiement}
            onChange={handleChange}
            margin="normal"
          />

          <TextField
            fullWidth
            label="Comments"
            name="commentaire"
            value={formData.commentaire}
            onChange={handleChange}
            margin="normal"
            multiline
            rows={3}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="secondary"
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Process Payment"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CashPaymentModal;
